import { Component } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { NgClass } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TsButtonModule, TsTableService } from 'tsui';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import moment from 'moment';
import { TsTableUtils } from 'tsui/lib/ts-table/ts-table.utils';
import formatNumber from 'accounting-js/lib/formatNumber.js';
import formatMoney from 'accounting-js/lib/formatMoney.js';

@Component({
  standalone: true,
  imports: [MatIconModule, NgClass, RouterModule, TsButtonModule, MatProgressSpinnerModule, NgClass],
  selector: 'button-renderer',
  template: `
    <div class="row-group">
      @if (isCategory || (isParent && !isChild)) {
        <span class="expand-icon" [ngClass]="{ expanded: isExpanded }">
          @if (params.column.colId === 'ag-Grid-AutoColumn') {
            <button class="expand-icon" (click)="onExpandClick()" mat-icon-button>
              <mat-icon class="expand-icon" [ngClass]="{ expanded: isExpanded }">
                {{ isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}
              </mat-icon>
            </button>
          }
        </span>
      }
      @if (isCategory) {
        <div class="row-group">
          {{ params.data?.label }}
          <span class="item-count">{{ params.data?.totalCount }}</span>
        </div>
      } @else {
        <div class="deals-container" [ngClass]="{ 'deal-title-child': params.data?.is_child === 'true' }">
          @if (
            params.data?.featured_image_url &&
            params.colDef?.field === 'title' &&
            params.column.colId === 'ag-Grid-AutoColumn'
          ) {
            <a [routerLink]="['../', params.data?.id, 'details', 'overview']">
              <img
                class="deal-image"
                loading="lazy"
                cursor-pointer
                [src]="params.data?.featured_image_url"
                (error)="onImageError($event)"
              />
            </a>
          }
          @if (params.colDef?.field === 'title' && params.column.colId === 'ag-Grid-AutoColumn') {
            <a
              data-cy="table-deal-name-label"
              [routerLink]="['../', params.data?.id, 'details', 'overview']"
              class="deal-title"
            >
              {{ params.value }}
            </a>
          } @else {
            <span>
              {{ params.colDef?.field === 'title' ? params.value : formatValue(params.colDef?.context?.fieldDef) }}
            </span>
          }
          @if (isLoading) {
            <mat-progress-spinner [diameter]="24" [mode]="'indeterminate'"></mat-progress-spinner>
          }
        </div>
      }
    </div>
  `,
  styles: [
    `
      .row-group {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
      }

      .item-count {
        display: flex;
        justify-content: center;
        background-color: #fff1e1;
        color: #904e00;
        font-weight: 600;
        font-size: 12px;
        padding: 5px;
        border-radius: 4px;
        height: 20px;
        padding: 2px 4px;
        line-height: 16px;
        align-items: center;
        border: 1px solid #ffd5a9;
      }

      .deals-container {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 4px;
      }
      .deal-title {
        text-decoration: none;
        color: inherit;
      }
      .deal-title-child {
        margin-left: 40px;
      }

      .deal-image {
        width: 30px;
        height: 20px;
        background-size: cover;
        flex-shrink: 0;
      }

      img {
        background-image: url('assets/images/placeholder.jpeg');
      }

      .expand-icon {
        cursor: pointer;
        display: flex;
        align-items: center;
      }
    `,
  ],
})
export class DealsGroupTableRenderer implements ICellRendererAngularComp {
  params;
  isCategory = false;
  isParent = false;
  isChild = false;
  isExpanded = false;
  isLoading = false;

  constructor(private tsTableService: TsTableService) {}

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.isCategory = this.params.data?.isCategory || false;
    this.isParent = this.params.data?.is_parent === 'true' || false;
    this.isChild = this.params.data?.is_child === 'true' || false;
    this.isExpanded = this.params.node.expanded;
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    this.isExpanded = this.params.node.expanded;
    return true;
  }

  onImageError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/images/placeholder.jpeg';
  }

  onExpandClick() {
    const node = this.params.node;
    const project = this.params.data;
    const context = this.params.colDef?.context;
    const index = node.rowIndex;
    const expandedProjects = context?.expandedProjects;

    this.isExpanded = !this.isExpanded;
    node.setExpanded(!node.expanded);

    if (project.is_parent === 'true' && !expandedProjects.includes(Number(project.id))) {
      context?.fetchChildren(project, index);
      this.isLoading = context.isLoadingChildren[index];
      project.loaded = true;
    }
  }

  formatValue(fieldDef) {
    switch (fieldDef?.type) {
      case 'date':
      case 'datetime': {
        const dateValue = this.tsTableService.getValueFromParam(fieldDef, this.params);
        return dateValue ? moment(dateValue).format('MM/DD/YYYY') : '';
      }
      case 'number': {
        const num = this.tsTableService.getValueFromParam(fieldDef, this.params);
        return TsTableUtils.formatNumberWithFieldRules(num, fieldDef);
      }
      case 'decimal': {
        const raw = this.tsTableService.getValueFromParam(fieldDef, this.params);
        if (!fieldDef.meta?.scale) {
          return raw;
        }
        const decimalValue = raw ? parseFloat(raw) : null;
        return decimalValue !== null ? formatNumber(decimalValue, { precision: fieldDef.meta.scale }) : '';
      }
      case 'money': {
        const raw = this.tsTableService.getValueFromParam(fieldDef, this.params);
        const moneyValue = raw !== null && raw !== undefined ? parseFloat(raw) : null;
        return moneyValue !== null ? formatMoney(moneyValue, { precision: fieldDef.meta?.scale || 0 }) : '';
      }
      case 'select': {
        if (fieldDef.name.includes('.')) {
          const nestedValues = this.tsTableService.getValueFromParam(this.params.data, fieldDef.name);
          if (nestedValues) {
            return nestedValues
              .map((nestedValue) => {
                const opt = this.tsTableService.findOptionById(
                  nestedValue.label,
                  this.params.colDef.cellEditorParams?.options,
                );
                return opt ? opt.name || opt.full_name : nestedValue.label;
              })
              .join(', ');
          }
          return '';
        }
        const options = this.params.colDef.cellEditorParams?.options || [];
        const sel = options.find((o) => o.id == this.params.value);
        return sel ? sel.name : this.params.value;
      }
      case 'text': {
        const txt = this.tsTableService.getValueFromParam(fieldDef, this.params);
        return txt;
      }
      default:
        return this.params.value;
    }
  }
}
